import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { Button, Flex, Spinner } from "@radix-ui/themes";
import { useFieldArray, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import RadioGroupComponent from "../../components/inputs/RadioGroup/RadioGroupComponent";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetAssignedTeachersClassesSubjectsQuery } from "../../hooks/useGetAssignedTeachersClassesSubjectsQuery";
import { useGetCurrentAcademicYearQuery } from "../../hooks/useGetCurrentAcademicYearQuery";
import { useGetTeacherEvaluationCriteriasQuery } from "../../hooks/useGetTeacherEvaluationCriteriasQuery";
import { resetTeacherData, resetUpdateTeachertData } from "../../redux/slices/school/teacher";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { createTeacherEvaluation, createTeachersEvaluationsCriterias } from "../../services/school/teacher/teacherEvaluationCriteria";
import { TeacherEvaluationType } from "../../types/TeacherEvaluation";
import toast from "react-hot-toast";
import { Key_teacherEvaluationCriterias } from "../../utils/constants";

export const EvaluateTeacher = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const mutationCreateTeacherEvaluation = useMutation({ mutationFn: createTeacherEvaluation, mutationKey: "createTeacherEvaluation" })
    const mutationCreateTeacherEvaluationsCriterias = useMutation({ mutationFn: createTeachersEvaluationsCriterias, mutationKey: "createTeachersEvaluationsCriterias" })
    const teacherData = useSelector((state: RootState) => state.teacher.teacher);
    const { data: currentAcademicYear, isLoading: isLoadingCurrentAcademicYear, isError: errorCurrentAcademicYear } = useGetCurrentAcademicYearQuery();

    const { control, setValue, getValues, handleSubmit, formState: { errors } } = useForm<{ idClass: string, description: string, idTeacher: string, idSubject: string, idTeacherSubjectClass?:string , teacherEvaluations: { score: number, idTeacherEvaluationCriteria: string, description: string, idTeacher: string, idSubject: string, idClass: string, idAcademicYear?: string,}[] }>({
        defaultValues: { idClass: '', idSubject: "", idTeacher: "", teacherEvaluations: [], description: '', idTeacherSubjectClass:''},
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
    });

    const { data: criterias, isLoading: isLoadingCriterias, isError: isErrorCriterias, refetch: refreshCriterias } = useGetTeacherEvaluationCriteriasQuery({ PageNumber: 1, PageSize: 400, IsForStudent: false, IdAcademicYear: currentAcademicYear?.id },
        {
            enabled: !!currentAcademicYear?.id
        }
    );
    
    const { data: classSubjectTeachers, isLoading: isLoadingClassSubjectTeachers, isError: isErrorClassSubjectTeachers, refetch: refreshClassSubjectTeacher } = useGetAssignedTeachersClassesSubjectsQuery(
        { PageNumber: 1, PageSize: 400, IdTeacher:teacherData?.id, IdAcademicYear: currentAcademicYear?.id },
        {
            enabled: !!teacherData?.id && !! currentAcademicYear?.id
        }
    )
    const { fields } = useFieldArray({
        control,
        name: 'teacherEvaluations',
    });
    const onSubmit = handleSubmit(async (data) => {
        const totalScore = data.teacherEvaluations.reduce((sum, evaluation) => Number(sum) + Number(evaluation.score), 0);
        const mediaScore = Math.round(totalScore / data.teacherEvaluations.length);
        const teacherEvaluation:TeacherEvaluationType = {
            idTeacher: data.idTeacher.split("/")?.[0] ?? "",
            classification: mediaScore,
            idAcademicYear: currentAcademicYear?.id!,
            idSubject: data.teacherEvaluations.find(te => te.idSubject)?.idSubject!,
            description: data.description,
            idClass: data.idClass,
        }

        try {
            const result = await mutationCreateTeacherEvaluation.mutateAsync(teacherEvaluation)
            if(!result) {toast.error(`Não foi possível guardar a avaliação`); return;}
            var scores = data.teacherEvaluations.map(te=> {
                    return {
                        idTeacherEvaluationCriteria: te.idTeacherEvaluationCriteria, 
                        idTeacherEvaluation: result, 
                        score: te.score,
                        status: true,
                    }
            });
             await Promise.all(scores!.map((cv) => {
                return mutationCreateTeacherEvaluationsCriterias.mutateAsync(cv)
              }))
            toast.success(`Avaliação guardada`);
        } catch (err: any) {
            toast.error(`${err?.message}`);
        }

    });
    const handleBack = () => {
        dispatch(resetTeacherData());
        dispatch(resetUpdateTeachertData());
        navigate(routes.teachers);
      }

      const handleTeacherSubjectClassChange = async (id: string) => {
        const {idClass, idSubject, idTeacher}   = classSubjectTeachers?.items.find(i=>i.id ===id) || {};
        setValue('idTeacherSubjectClass', id);
        if(idClass && idSubject && idTeacher){
            setValue('idClass', idClass);
            setValue('idTeacher', idTeacher);
            setValue('idSubject', idSubject);
            queryClient.invalidateQueries(Key_teacherEvaluationCriterias);
            await refreshCriterias();
            if (criterias && criterias.items) {
                setValue('teacherEvaluations',
                    criterias.items.filter(i=> !i.isForStudent).map(criteria => {
                        return {
                            score: 1,
                            idTeacherEvaluationCriteria: criteria?.id!,
                            description: criteria.description,
                            idClass: idClass,
                            idTeacher: idTeacher,
                            idSubject: idSubject,
                            idAcademicYear: currentAcademicYear?.id
                        }
                    })
                )
            }
        }
    }
    
    return (
        <form onSubmit={onSubmit} noValidate>
            <Flex direction='column' gap='4'>
                <Flex gap='4'>
                   
                    <SelectComponent
                        label="Docente | Turma | Disciplina"
                        options={classSubjectTeachers?.items.map((sc, index) => ({
                            value:`${sc.id}`,
                            label: `${sc.teacher} | ${sc.subject} ${sc.class}`
                        }))}
                        errors={errors}
                        control={control}
                        itemsError={isErrorClassSubjectTeachers}
                        itemsLoading={isLoadingClassSubjectTeachers}
                        name={"idTeacherSubjectClass"}
                        onSelect={handleTeacherSubjectClassChange}
                        style={{ width: '100%' }}
                    />
                </Flex>
                {fields?.map((criteria, index) => (
                    <Flex key={`${criteria.idTeacherEvaluationCriteria}-${index}`} direction="column" gap="2">
                        <Flex>
                            <RadioGroupComponent
                                name={`teacherEvaluations.${index}.score`}
                                label={criteria.description}
                                control={control}
                                errors={errors}
                                style={{ width: '100%' }}
                                options={[
                                    { value: "1", label: "Péssimo" },
                                    { value: "2", label: "Mau" },
                                    { value: "3", label: "Normal" },
                                    { value: "4", label: "Bom" },
                                    { value: "5", label: "Excelente" }
                                ]}
                                fieldProps={{
                                    style: { display: 'flex', flexDirection: 'row', gap: '16px' }
                                }}
                                itemsLoading={isLoadingCriterias}
                                itemsError={isErrorCriterias}
                            />
                        </Flex>
                    </Flex>
                ))}
                <Flex direction='row' gap='3' justify='end'>
              <Button type='button' variant='soft' color='gray' disabled={mutationCreateTeacherEvaluation.isLoading || mutationCreateTeacherEvaluationsCriterias.isLoading} onClick={() => {
                handleBack();
              }}
              >
                <ChevronLeftIcon /> Voltar
              </Button>
              <Button type='submit'
                disabled={mutationCreateTeacherEvaluation.isLoading || mutationCreateTeacherEvaluationsCriterias.isLoading}

              >
                {mutationCreateTeacherEvaluation.isLoading || mutationCreateTeacherEvaluationsCriterias.isLoading ? <Spinner /> : <EnterIcon />}
                Registar
              </Button>
            </Flex>
            </Flex>
        </form>

    )
}
